// React Dependencies
import { useMemo, useState, useEffect, useRef } from 'react';
// Common Dependencies
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
// Component
import { Form, Row, Col, Button, Stack } from 'react-bootstrap';
import { TNButton, TNTable } from '@common/components';
// Alert
import { toast } from 'react-toastify';
// Font Awesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
// Assets
import 'react-confirm-alert/src/react-confirm-alert.css';
// API
import { useListDatabase, useDeleteDatabase, useRouteAccessList } from '@hooks';
import CommonSEOTitle from '../../../../helpers/CommonSEOTitle';
import { getStoredData, onDefaultError, setPageMoveToFirstPage } from '../../../../helpers/commonHelpers';
import { useCheckUserPermission, useShowProject } from '../../../../hooks';
import TabNavigation from '../../../../common/components/TabNavingation';
import { ProjectView } from '../ProjectTabs';
import CloseIcon from '../../../../assets/images/svg/close.svg';
import SearchIcon from '../../../../assets/images/svg/search.svg';
import AddIcon from '../../../../assets/images/svg/add.svg';

import viewIcon from '../../../../assets/images/svg/table-view.svg';
import deleteIcon from '../../../../assets/images/svg/table-delete.svg';
import editIcon from '../../../../assets/images/svg/table-edit.svg';
//for new table design
import '../../../../assets/scss/components/_new_table.scss';
import { DatabaseShimmer } from '../../PageShimmers/ProjectTabShimmer';
import {
  getStoredDataUsingKey,
  setAllStoredData,
  removeStoredData
} from '../../../../helpers/localStorageHelper';

const ListDatabasePage = ({ t, setTabLoading, alert }) => {
  // Navigate
  const navigate = useNavigate();

  useCheckUserPermission(
    ['projects.index'],
    () => {},
    (error) => {
      onDefaultError(error,false);
    }
  );

  // Project ID
  const { projectId: id } = useParams();
  const storageKey = `adminDatabasePost_${id}`;

  const [projectData, setProjectData] = useState(null);
  useShowProject(
    id,
    ({ data: project }) => {
      if (project) {
        setProjectData(project);
      }
    },
    (error) => {
      onDefaultError(error);
    }
  );
  // Refs
  const searchDebounceTimeout = useRef(null);

  // States
  const [data, setData] = useState([]);
  const [projectIdState, setProjectId] = useState(id);
  const [currentPage, setCurrentPage] = useState(getStoredData(storageKey, 'currentPage', 1));
  const [paginationData, setPaginationData] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(
    getStoredDataUsingKey(storageKey, 'selectedStatus', '')
  );
  const [searchText, setSearchText] = useState(getStoredData(storageKey, 'searchText', ''));
  const [searchValue, setSearchValue] = useState(searchText);

  const [routeName, setRouteNames] = useState([]);
  // Project ID State Set
  useEffect(() => {
    if (id) {
      setProjectId(id);
      setTabLoading(true);
    }
  }, [id]);
  // List Filter Set On Load
  useEffect(() => {
    let adminDatabasePost = {
      projectId: projectIdState,
      searchText: searchText,
      currentPage: currentPage,
      selectedStatus: selectedStatus
    };
    setAllStoredData(storageKey, adminDatabasePost, false);
  }, [projectIdState, currentPage, searchText, selectedStatus]);
  // Get Records
  const { refetch } = useListDatabase(
    projectIdState,
    currentPage,
    searchText,
    selectedStatus,
    (res) => {
      if (res && res.data && res.data.database_list && res.data.pagination) {
        setData(res.data.database_list);
        setPaginationData(res.data.pagination);
        setTabLoading(false);
        if (res.data.database_list.length === 0 && currentPage > 1) {
          setPageMoveToFirstPage(setCurrentPage, storageKey);
        }
      } else {
        console.error('Invalid API response:', res);
      }
    },
    (error) => {
      onDefaultError(error);
    }
  );
  // Route
  useRouteAccessList((res) => {
    setRouteNames(res.data.route_name);
  });
  const shouldShowRoute = (route) => {
    if (routeName) {
      return routeName.includes(route);
    }
    return false;
  };
  // Action Handler
  const handleEditClick = (event, projectId, id) => {
    // navigate(`/projects/view/${projectId}/database/${id}/edit`);
    const url = `/projects/view/${projectId}/database/${id}/edit`;
    if (event.ctrlKey || event.metaKey) {
      // Ctrl + Click (Windows/Linux) or Cmd + Click (Mac) => Open in new tab
      window.open(url, '_blank');
    } else {
      // Normal click => Navigate in same tab
      navigate(url);
    }
  };
  const handleViewClick = (event, projectId, databaseId) => {
    // navigate(`/projects/view/${projectId}/database/${databaseId}/tables`);
    const url = `/projects/view/${projectId}/database/${databaseId}/tables`;
    if (event.ctrlKey || event.metaKey) {
      // Ctrl + Click (Windows/Linux) or Cmd + Click (Mac) => Open in new tab
      window.open(url, '_blank');
    } else {
      // Normal click => Navigate in same tab
      navigate(url);
    }
  };
  // Delete Database API
  const { deleteDatabase } = useDeleteDatabase();
  // Delete Database Handler
  const handleDeleteClick = (projectId, id) => {
    alert({
      type: 'warning',
      title: 'Confirmation',
      message: t('page.reset_alert_popup_message'),
      confirmText: t('page.alert_popup_yes_button'),
      cancelText: t('page.alert_popup_no_button'),
      onConfirm: async () => {
        try {
          const response = await deleteDatabase(projectId, id);
          if (response && response.message) {
            toast.success(response.message);
          }
          refetch();
        } catch (error) {
          toast.error(t('admin.database_deleted_error'));
        }
      }
    });
  };
  // Table Action Columns
  const actionColumn = {
    Header: `${t('page.action_column')}`,
    accessor: 'database_id',
    Cell: ({ value: id }) => {
      return (
        <div className="d-flex align-items-center" style={{ minHeight: 'inherit', gap: '12px' }}>
          {shouldShowRoute('projects.index') && (
            <TNButton
              className="table-button view-btn"
              onClick={(event) => handleViewClick(event, projectIdState, id)}>
              {/* {t('page.action_button_text_view')} */}
              <img src={`admin/${viewIcon}`}></img>
            </TNButton>
          )}
          {shouldShowRoute('projects.index') && (
            <TNButton
              className="table-button edit-btn"
              onClick={(event) => handleEditClick(event, projectIdState, id)}>
              {/* {t('page.action_button_text_edit')} */}
              <img src={`admin/${editIcon}`}></img>
            </TNButton>
          )}
          {shouldShowRoute('projects.index') && (
            <TNButton
              className="table-button delete-btn"
              onClick={() => handleDeleteClick(projectIdState, id)}>
              {/* {t('page.action_button_text_delete')} */}
              <img src={`admin/${deleteIcon}`}></img>
            </TNButton>
          )}
        </div>
      );
    },
    disableSortBy: true
  };
  let showActionColumn =
    shouldShowRoute('projects.index') ||
    shouldShowRoute('projects.index') ||
    shouldShowRoute('projects.index');
  // Table Columns
  const columnsjson = [
    {
      Header: `${t('page.database_created_at')}`,
      accessor: 'created_at',
      disableSortBy: true,
      Cell: ({ value }) => <div style={{ whiteSpace: 'nowrap' }}>{value}</div>
    },
    {
      Header: `${t('page.database_name_label')}`,
      accessor: 'name',
      disableSortBy: true
    }
  ];
  if (showActionColumn) {
    columnsjson.push(actionColumn);
  }
  const columns = useMemo(() => columnsjson, [routeName]);
  if (paginationData === null) {
    return null;
  }
  const pageIndexHandle = (pageIndex) => {
    setCurrentPage(pageIndex + 1);
  };
  const handleSearch = (search_text) => {
    const value = search_text.target.value;
    setSearchValue(value);

    if (searchDebounceTimeout.current) {
      clearTimeout(searchDebounceTimeout.current);
    }

    searchDebounceTimeout.current = setTimeout(() => {
      setCurrentPage(1);
      setSearchText(searchValue);
    }, 500);
  };
  const handleClearFilter = () => {
    setSearchValue('');
    setSearchText('');
    setSelectedStatus('');
    setCurrentPage(1);
    removeStoredData(storageKey);
  };
  const addDatabase = (event) => {
    // navigate(`/projects/view/${projectIdState}/database/add`);

    const url = `/projects/view/${projectIdState}/database/add`;
    if (event.ctrlKey || event.metaKey) {
      // Ctrl + Click (Windows/Linux) or Cmd + Click (Mac) => Open in new tab
      window.open(url, '_blank');
    } else {
      // Normal click => Navigate in same tab
      navigate(url);
    }
  };
  const tabs = ProjectView(id, t);

  return (
    <>
      <CommonSEOTitle title={t('page.database_list_label')} />
      {console.log(data, 'data-------')}
      {data !== null ? (
        <Stack className="database-page">
          <Stack className="flex-row flex-wrap filter-row mt-3">
            <Form.Group controlId="search" className="position-relative search-input-group h-100">
              <img
                src={`admin/${SearchIcon}`}
                className="search-icon position-absolute top-50 translate-middle-y"></img>
              <Form.Control
                placeholder="Search"
                onKeyUp={handleSearch}
                onChange={handleSearch}
                className="search-input"
                value={searchValue}
              />
            </Form.Group>
            <Button
              type="button"
              onClick={handleClearFilter}
              className=" cancel-search-btn"
              disabled={searchText || selectedStatus ? false : true}>
              <img src={`admin/${CloseIcon}`}></img>
              {t('page.cancel_search_button')}
            </Button>
            {shouldShowRoute('projects.index') && (
              <TNButton
                className="table-add-button new m-0 ms-auto"
                loading={false}
                type="button"
                onClick={(event) => addDatabase(event)}>
                <img src={`admin/${AddIcon}`}></img>
                {t('page.database_add_button')}
              </TNButton>
            )}
          </Stack>
          <TNTable
            columns={columns}
            data={data}
            paginationData={paginationData}
            onSelectPage={pageIndexHandle}
            t={t}
            pageIndexGet={currentPage - 1}
            key={Math.floor(Math.random() * (1000 - 1 + 1) + 1)}
          />
        </Stack>
      ) : (
        <>
          <DatabaseShimmer />
        </>
      )}
    </>
  );
};
ListDatabasePage.propTypes = {
  columns: PropTypes.any,
  data: PropTypes.any,
  paginationData: PropTypes.any,
  t: PropTypes.func,
  row: PropTypes.any,
  value: PropTypes.any
};
export default ListDatabasePage;
